import React, { useState } from 'react';
import { Helmet } from 'react-helmet';

// Corrected import path
import '../App.css'; // Adjust the import path as necessary
import { QuestionCard } from '../components/QuestionCard';
import { BudgetSlider } from '../components/BudgetSlider';
import './Questionnaire.css';
import { Accordion } from '../components/Accordion';

// --- Data Structures (questions array remains the same) ---
const questions = [
    {
        id: 'useCase',
        ques_no:'Question 1',
        question: 'What will be the primary use for your PC?',
        type: 'multiple',
        options: [
          { title: 'General Use', description: 'Web browsing, office apps, media.', icon: '🌐' },
          { title: 'Gaming', description: 'Playing the latest AAA and esports titles.', icon: '🎮' },
          { title: 'Streaming & Content Creation', description: 'Video editing, streaming, design.', icon: '🎬' },
          { title: 'Workstation & AI', description: 'Heavy computation, data science, AI/ML.', icon: '💼' },
          { title: 'Programming', description: 'Compiling code, running IDEs, projects.', icon: '💻' },
          { title: 'Video Editing', description: 'High-resolution editing and rendering.', icon: '🎥' },
          { title: '3D Modeling & Design', description: 'Creating 3D models, sculpting, rendering.', icon: '🧊' },
          { title: 'VFX', description: 'Intensive visual effects and simulations.', icon: '✨' },
          { title: 'Graphic Design', description: 'Digital illustration, photo manipulation.', icon: '🖌️' },
          { title: 'Crypto Mining', description: 'Optimized for mining cryptocurrency.', icon: '🪙' },
          { title: 'Home Server', description: 'File storage, media streaming (Plex).', icon: '🗄️' },
          { title: 'Game Development', description: 'Building, compiling, and testing games.', icon: '🎲' },
          { title: 'CAD/CAM & Engineering', description: 'Precision drafting and engineering simulations.', icon: '⚙️' }
        ],
    },

    // 6. Storage Amount (Capacity tradeoff)
    {
      id: 'storageAmount',
      ques_no:'Question 2',
      question: "How much storage space do you want?",
      type: 'single',
      options: [
        { title: 'Basic (Under 500GB)', description: 'Docs, a few apps (cheapest).', icon: '📄' },
        { title: 'Standard (500GB - 1TB)', description: 'Games/photos, average cost.', icon: '📸' },
        { title: 'Large (2TB - 4TB)', description: 'Many games or videos (higher cost).', icon: '🎬' },
        { title: 'Massive (4TB+)', description: 'Professional use, max cost.', icon: '🗄️' }
      ],
    },

    // 7. Storage Expansion (Expansion = bigger/costlier)
    {
      id: 'storageExpansion',
      ques_no:'Question 3',
      question: "Think you'll add storage in the near future?",
      type: 'single',
      options: [
        { title: 'I am likely to do so', description: 'Bigger storage costs more.', icon: '➕' },
        { title: 'The initial amount is enough', description: 'I might upgrade when needed.', icon: '🔒' }
      ],
    },

    // User Overall Priority
    {
      id: "userPriority",
      ques_no:'Question 4',
      question: "What matters most in your build?",
      type: "single",
      options: [
        {
          title: "Maximum performance",
          description: "Top speed, even if it's bigger and costs more.",
          icon: "⚡"
        },
        {
          title: "Lowest price",
          description: "Keep cost as low as possible.",
          icon: "💸"
        },
        {
          title: "Balanced & simple setup",
          description: "Value for money around my Budget.",
          icon: "📦"
        }
      ]
    },

    {
    id: 'brandPreferenceCpu',
    ques_no:'Question 5',
    question: 'Do you want a specific CPU brand, or just best value for money?',
    type: 'single',
    options: [
      { title: 'Intel', description: 'Strong for speed, office & gaming.', icon: '🔵' },
      { title: 'AMD', description: 'Great for multitasking and price.', icon: '🔴' },
      { title: 'No Preference', description: 'Pick best for my budget.', icon: '🤷' },
    ],
  },

  // 3. GPU Brand Preference (Unchanged, clear labels)
  {
    id: 'brandPreferenceGpu',
    ques_no:'Question 6',
    question: 'Want a specific graphics card brand, or whichever is best value?',
    type: 'single',
    options: [
      { title: 'NVIDIA', description: 'Known for extra features, top games.', icon: '🟢' },
      { title: 'AMD', description: 'Best for price-to-performance.', icon: '🔴' },
      { title: 'No Preference', description: 'Pick best for my budget.', icon: '🤷' },
    ],
  },

];



// --- Results Page Component (Corrected and Reordered) ---
const ResultsPage = ({ builds, onStartOver, isLoading, error }) => {
  const getComponentIcon = (type) => {
    const icons = {
      CPU: '🧠', GPU: '🎮', Motherboard: '🛠️', RAM: '💾', 
      SSD: '🚀', HDD: '💽', Cooler: '❄️', PSU: '⚡', Cabinet: '📦',
    };
    return icons[type] || '⚙️';
  };

  // This defines the exact order for displaying components
  const componentOrder = ['CPU', 'GPU', 'Motherboard', 'RAM', 'SSD', 'HDD', 'Cooler', 'PSU', 'Cabinet'];

  return (
    <div className="results-page-container">
      <div className="results-header">
        <h1>Your Custom PC Builds</h1>
        <p>Our algorithm has created these unique builds based on your needs. Click on any option to see the full parts list.</p>
      </div>

      {isLoading && <p>Loading...</p>}
      {error && <p className="error-text">{error}</p>}
      
      {!isLoading && !error && builds && builds.length > 0 && (
        <div className="builds-wrapper">
          {builds.map((build, index) => (
            <Accordion 
              key={index}
              title={`Build ${index + 1}: The Value Champion - ₹${Math.round(build.TotalPrice).toLocaleString('en-IN')}`}
            >
              <ul className="component-list">
                {componentOrder.map(componentKey => {
                  const component = build[componentKey];
                  const componentType = componentKey === 'Storage' ? 'SSD' : componentKey;
                  
                  // Handle HDD and Cabinet placeholders
                  if (!component) {
                    
                    if (componentKey === 'Cabinet') {
                       return (
                        <li key={componentKey} className="placeholder">
                          <span className="component-icon">{getComponentIcon(componentType)}</span>
                          <span className="component-type">{componentType}</span>
                          <span className="component-name">To be determined</span>
                        </li>
                      );
                    }
                    return null;
                  }

                  // This is the corrected part for displaying the name
                  const name = component.Processor || component.Name || component.name;
                  const price = component.price || component.PriceINR || component.Price || 0;

                  return (
                    <li key={componentKey}>
                      <span className="component-icon">{getComponentIcon(componentType)}</span>
                      <span className="component-type">{componentType}</span>
                      <span className="component-name">{name}</span>
                      <span className="component-price">₹{price.toLocaleString('en-IN')}</span>
                      {component.link && (
                        <a
                          href={component.link}
                          target="_blank"
                          rel="nofollow sponsored noopener noreferrer"
                          className="amazon-button"
                        >
                          View on Amazon
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Accordion>
          ))}
        </div>
      )}

     {!isLoading && !error && (!builds || builds.length === 0) && (
        <div className="no-builds-found">
            <h3>No Builds Found</h3>
            <p>We couldn't find a build that matches your specific criteria and budget. Please try adjusting your selections.</p>
        </div>
    )}

    <div className="next-step-container">
      <button className="next-button" onClick={onStartOver}>
        Start Over
      </button>
    </div>
  </div>
  );
};


// --- Main Questionnaire Component ---
export const Questionnaire = () => {
  const [answers, setAnswers] = useState({});
  const [budget, setBudget] = useState(100000);
  const [view, setView] = useState('questionnaire');
  const [finalBuilds, setFinalBuilds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showWarning, setShowWarning] = useState(false);

  const handleSelectAnswer = (questionId, answerTitle, questionType) => {
    setAnswers(prevAnswers => {
      const newAnswers = { ...prevAnswers };
      if (questionType === 'multiple') {
        const currentAnswers = newAnswers[questionId] || [];
        if (currentAnswers.includes(answerTitle)) {
          newAnswers[questionId] = currentAnswers.filter(ans => ans !== answerTitle);
        } else {
          newAnswers[questionId] = [...currentAnswers, answerTitle];
        }
      } else {
        newAnswers[questionId] = answerTitle;
      }
      return newAnswers;
    });
  };

  const handleFinish = async () => {
    const unansweredQuestions = questions.filter(q => {
        const answer = answers[q.id];
        return !answer || (Array.isArray(answer) && answer.length === 0);
    });

    if (unansweredQuestions.length > 0) {
        setShowWarning(true);
        return;
    }

    setShowWarning(false);
    const finalPreferences = { ...answers };
    console.log("--- FINAL PREFERENCES SENT TO BACKEND ---");
    console.log({ budget, preferences: finalPreferences });

    setIsLoading(true);
    setError('');
    setView('results');

    try {
        const response = await fetch('https://pcbuildguru-api.onrender.com/api/generate-build', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ budget, preferences: finalPreferences }),
        });

        if (!response.ok) {
            throw new Error('Something went wrong with the server.');
        }

        const data = await response.json();
        setFinalBuilds(data.builds);

    } catch (err) {
        console.error("Fetch Error:", err);
        setError("Failed to generate builds. The server might be offline or an error occurred.");
        setFinalBuilds([]);
    } finally {
        setIsLoading(false);
    }
  };
  
  const handleStartOver = () => {
      setAnswers({});
      setBudget(100000);
      setView('questionnaire');
      setFinalBuilds([]);
      setError('');
      setShowWarning(false);
  }

  if (view === 'results') {
    return (
      <>
        <Helmet>
          <title>PC Build Results – PC Build Guru</title>
          <meta name="description" content="View expert-recommended PC builds based on your answers. Compare components, prices, and performance." />
          <meta name="keywords" content="PC build results, PC parts list, custom PC recommendations, gaming PC builds" />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href="https://www.pcbuildguru.com/results" />
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": (finalBuilds || []).map((b, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "name": b?.BrandCombo || `Build ${i + 1}`,
              "url": "https://www.pcbuildguru.com/results"
            }))
          })}</script>

        </Helmet>
        <ResultsPage builds={finalBuilds} onStartOver={handleStartOver} isLoading={isLoading} error={error} />
      </>
    );
  }


  // *** THIS IS THE MAIN STRUCTURAL CHANGE ***
  return (
    <div className="page-container">
      <Helmet>
        <title>Build Your Dream PC – PC Build Guru</title>
        <meta name="description" content="Answer a few simple questions to get personalized PC build recommendations tailored to your budget and needs." />
        <meta name="keywords" content="PC builder questionnaire, build a PC, gaming PC builder, workstation PC builder" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.pcbuildguru.com/questionnaire" />
      </Helmet>

        <div className="main-content">
            {questions.map((question) => {
                const selectedAnswer = answers[question.id];
                return (
                <div key={question.id} className="question-section">
                    <h3 className="question-number">{question.ques_no}</h3>
                    <h2>{question.question}</h2>
                    <div className="card-container">
                    {question.options.map(option => {
                        let isSelected = false;
                        if (question.type === 'multiple') {
                        isSelected = selectedAnswer?.includes(option.title);
                        } else {
                        isSelected = selectedAnswer === option.title;
                        }
                        
                        return (
                        <QuestionCard
                            key={option.title}
                            title={option.title}
                            description={option.description}
                            icon={option.icon}
                            isSelected={isSelected}
                            onSelect={() => handleSelectAnswer(question.id, option.title, question.type)}
                        />
                        );
                    })}
                    </div>
                </div>
                );
            })}

            <div className="question-section">
                <h2>What is your approximate budget?</h2>
                <BudgetSlider 
                      value={budget} 
                      onChange={setBudget}
                      min="60000"
                      max="600000"
                      step="10000"
                />
            </div>

            <div className="next-step-container">
                {showWarning && (
                    <p className="warning-message">
                        Please answer all questions before proceeding.
                    </p>
                )}
                <button 
                    className="next-button" 
                    onClick={handleFinish}
                >
                Finish & See Builds
                </button>
            </div>
        </div>
        
        <aside className="sidebar">
            <h3>Tips & Recommendations</h3>
            <p>
                As you answer the questions, we'll provide helpful tips here. For example, if you select "Gaming," we might recommend prioritizing your GPU budget.
            </p>
            {/* This is where your contextual tips will go */}
        </aside>
    </div>
  );
};